import type { ElementType, ReactNode } from 'react';

import { cn } from '@giyus/ui';

export interface PageHeaderProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Texto curto acima do título (módulo, trilha). */
  readonly eyebrow?: ReactNode;
  /** Status da página/registro (ex.: `StatusBadge`), ao lado do título. */
  readonly status?: ReactNode;
  /** Ações principais (ex.: `Button`), alinhadas ao fim. */
  readonly actions?: ReactNode;
  readonly headingLevel?: 'h1' | 'h2';
  readonly className?: string;
}

/** Cabeçalho de página: um único `h1` por tela, descrição, status e ações. */
export function PageHeader({
  title,
  description,
  eyebrow,
  status,
  actions,
  headingLevel = 'h1',
  className,
}: PageHeaderProps) {
  const Heading: ElementType = headingLevel;
  return (
    <header
      data-slot="page-header"
      className={cn('flex flex-wrap items-start justify-between gap-x-6 gap-y-3', className)}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {eyebrow ? (
          <p className="border-l-2 border-brand pl-2.5 text-caption font-semibold tracking-[0.18em] text-heading uppercase leading-tight">
            {eyebrow}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <Heading className="min-w-0 break-words text-headline font-semibold text-heading">
            {title}
          </Heading>
          {status}
        </div>
        {description ? (
          <p className="max-w-prose text-body text-foreground-secondary">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}
